import './styles.css'

import { MouseEvent, useCallback, useContext, useRef } from 'react'

import { Box, Button, Tooltip as ChakraTooltip, HStack, Menu, Portal, Span, Spinner, Text } from '@chakra-ui/react'
import { ChevronDownIcon } from '@editor/assets/icons'
import { BUTTON_FONT_SIZE, EDITOR_TOOLS, MODAL_TYPES } from '@editor/config/constants'
import { GlobalModalContext } from '@editor/contexts/GlobalModal'
import { InspectionArea } from '@editor/interfaces/inspection'
import { selectCanChangeAreaForPolylineTool } from '@editor/pages/projects/editor/tools/Polyline/store/selectors'
import { usePolylineSelector } from '@editor/pages/projects/editor/tools/store/selector'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import { setAreaSwitched } from '@editor/store/page'
import { Tooltip } from '@infraloop/common'
import { startCase } from 'lodash'
import mixpanel from 'mixpanel-browser'
import { Link, useLocation, useMatches, useNavigate } from 'react-router'

/**
 * Width of the switcher trigger, and the minimum width of its dropdown. The dropdown is
 * portalled out of the switcher's DOM, so it can't read this off the trigger via CSS — both
 * sides read it from here instead.
 */
const SWITCHER_WIDTH = '280px'

/** Upper bound on how far the dropdown may grow to fit a long name before it wraps instead. */
const SWITCHER_MENU_MAX_WIDTH = 'min(90vw, 40rem)'

export interface InspectionAreaSwitcher extends InspectionArea {
  namePostfix?: string | React.ReactNode
  disabled?: boolean
  hidden?: boolean
  tooltip?: string | React.ReactNode
  tooltipPlacement?: Required<ChakraTooltip.RootProps>['positioning']['placement']
  isLeft?: boolean
  isRight?: boolean
}

const AreaSwitcher = ({
  containerId = 'area-switcher',
  inspectionAreas,
  onSwitched,
  currentArea,
}: {
  containerId?: string
  inspectionAreas: InspectionAreaSwitcher[]
  onSwitched?: (area: InspectionAreaSwitcher) => void
  currentArea?: InspectionAreaSwitcher | null
}) => {
  // Context
  const { showModal } = useContext(GlobalModalContext)

  // Refs
  const containerRef = useRef<HTMLDivElement>(null)

  // Router
  const navigate = useNavigate()
  const location = useLocation()
  const matches = useMatches()
  const isEditorPage = matches.some((row) => row.id.includes('editor'))
  const isInspectionSheetPage = matches.some((row) => row.id.includes('inspection-sheet'))

  // Store
  const dispatch = useAppDispatch()
  const inspectionArea = useAppSelector((state) => state.page.inspectionArea)
  const isLoading = useAppSelector((state) => state.page.isLoading)
  const selectedTool = useAppSelector((state) => state.editor.selectedTool)
  const cuboidAnchor = useAppSelector((state) => state.maskPCD.workingMaskCuboid)
  const intervals = useAppSelector((state) => state.toolGrid.intervals)
  const toolVolumePolygonIsDirty = useAppSelector((state) => state.toolVolumeEstimationPolygon.isDirty)
  const toolRebarDetectionIsDirty = useAppSelector((state) => state.toolRebarDetection.isDirty)
  const toolPlaneDetectionIsDirty = useAppSelector((state) => state.toolPlaneDetection.isDirty)
  const toolCameraProfileIsDirty = useAppSelector((state) => state.toolCameraProfile.isDirty)
  const canChangePolylineTool = usePolylineSelector(selectCanChangeAreaForPolylineTool)

  // Derived
  const childPage = location.pathname.split('/').pop()

  const areas = [...inspectionAreas]
  // Re-sort inspection areas with editedInspectionAreas if we're on inspection sheet
  const editedInspectionAreas = useAppSelector((state) => state.inspectionSheet.editedInspectionAreas)
  if (isInspectionSheetPage) {
    areas.sort((a, b) => {
      const editedA = editedInspectionAreas.find((edited) => edited.inspection_area_id === a.inspection_area_id)
      const editedB = editedInspectionAreas.find((edited) => edited.inspection_area_id === b.inspection_area_id)
      if (editedA && editedB) {
        return editedA.order! - editedB.order!
      }
      if (editedA) {
        return -1
      }
      if (editedB) {
        return 1
      }
      return a.order! - b.order!
    })
  }

  /**
   * Get URL for an area.
   */
  const getUrl = useCallback(
    (area: InspectionArea) => `/projects/${area.project_id}/${childPage}?area=${area.inspection_area_id}`,
    [childPage],
  )

  /**
   * Navigate to an area and track the event.
   */
  const navigateToArea = useCallback(
    (area: InspectionAreaSwitcher) => {
      if (onSwitched) {
        onSwitched(area)
        return
      }

      dispatch(setAreaSwitched(area.inspection_area_id))

      mixpanel.track('Switch area', {
        'Inspection Area ID': inspectionArea?.inspection_area_id,
        'Project ID': area.project_id,
        'Navigating To Area ID': area.inspection_area_id,
        Page: childPage ? startCase(childPage) : null,
      })
      void navigate(getUrl(area), {
        state: {
          navigated: true,
        },
      })
    },
    [childPage, getUrl, navigate, onSwitched, dispatch, inspectionArea],
  )

  /**
   * Handle switching area
   */
  const handleSwitchArea = useCallback(
    (event: MouseEvent<HTMLDivElement>, area: InspectionArea) => {
      event.preventDefault()
      event.stopPropagation()

      // Check for dirty states before switching area.
      // Only applicable for editor page.
      if (
        isEditorPage &&
        (cuboidAnchor ||
          Object.keys(intervals).length ||
          toolVolumePolygonIsDirty ||
          toolRebarDetectionIsDirty ||
          toolPlaneDetectionIsDirty ||
          toolCameraProfileIsDirty ||
          (!canChangePolylineTool && selectedTool === EDITOR_TOOLS.POLYLINE))
      ) {
        showModal({
          modalType: MODAL_TYPES.CONFIRMATION_CRITICAL,
          body: '検出を破棄しますか？',
          confirmText: '破棄',
          cancelText: 'キャンセル',
          onConfirm: () => {
            navigateToArea(area)
            return true
          },
        })
        return
      }

      navigateToArea(area)
    },
    [
      selectedTool,
      isEditorPage,
      cuboidAnchor,
      intervals,
      toolVolumePolygonIsDirty,
      toolRebarDetectionIsDirty,
      toolPlaneDetectionIsDirty,
      toolCameraProfileIsDirty,
      canChangePolylineTool,
      showModal,
      navigateToArea,
    ],
  )

  return (
    <Box ref={containerRef}>
      <HStack id="area-switcher" gap={4}>
        <Text className="label" fontSize={BUTTON_FONT_SIZE}>
          エリア
        </Text>
        <Menu.Root positioning={{ placement: 'bottom-end' }}>
          <Menu.Trigger asChild>
            <Button data-testid="area-switcher-button" width={SWITCHER_WIDTH}>
              {/* The name slot is a flex item, so its automatic minimum width is the full width of the
                  name. Without min-width 0 a long name pushes out of the fixed-width trigger — spilling
                  over the label beside it and shoving the chevron out — instead of ellipsing. */}
              <Span flex={1} minW={0}>
                {isLoading ? <Spinner size="sm" position="relative" top="2px" /> : null}
                <Span className="area-name">
                  {currentArea !== null
                    ? currentArea?.inspection_area_name || inspectionArea?.inspection_area_name
                    : null}
                  {currentArea === null ? 'エリアを選択' : null}
                </Span>
              </Span>
              <ChevronDownIcon />
            </Button>
          </Menu.Trigger>
          <Portal>
            <Menu.Positioner>
              <Menu.Content
                className="scrollable"
                maxHeight={{ base: '90svh', xl: '50svh' }}
                bg="gray2.800"
                p={0}
                border="1px solid"
                borderColor="gray2.600"
                borderRadius="md"
                overflow="auto"
                // Names are often one long unbreakable token. Rather than scroll them sideways, the
                // list grows to fit the longest one — down to the trigger's width, up to a bound past
                // which a name wraps instead of running off the screen.
                width="max-content"
                minWidth={SWITCHER_WIDTH}
                maxWidth={SWITCHER_MENU_MAX_WIDTH}
                overflowWrap="anywhere"
              >
                {areas
                  .filter((ar) => !ar.hidden)
                  .map((area) => (
                    <Tooltip
                      key={`${containerId}-${area.inspection_area_id}`}
                      label={area.tooltip as string}
                      showArrow
                      positioning={{ placement: area.tooltipPlacement }}
                      portalRef={containerRef}
                    >
                      {area.disabled ? (
                        <Menu.Item asChild value={area.inspection_area_id} disabled color="gray2.300" px={3} py={2.5}>
                          {area.inspection_area_name} {area.namePostfix}
                        </Menu.Item>
                      ) : (
                        <Menu.Item
                          asChild
                          value={area.inspection_area_id}
                          onClick={(event) => handleSwitchArea(event, area)}
                          color="gray2.300"
                          _hover={{ bg: 'gray2.700' }}
                          px={3}
                          py={2.5}
                          cursor="pointer"
                        >
                          <Link to={getUrl(area)}>
                            {area.inspection_area_name} {area.namePostfix}
                          </Link>
                        </Menu.Item>
                      )}
                    </Tooltip>
                  ))}
              </Menu.Content>
            </Menu.Positioner>
          </Portal>
        </Menu.Root>
      </HStack>
    </Box>
  )
}

export default AreaSwitcher
